import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/lib/store";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RotateCcw, Copy, Save, Printer, Share2 } from "lucide-react";

export default function Calculator({ tab: initialTab }) {
  const { slug } = useParams();
  const { user } = useApp();
  const [tab, setTab] = useState(initialTab || "calc");
  const [vialMg, setVialMg] = useState("");
  const [diluentMl, setDiluentMl] = useState("");
  const [doseMcg, setDoseMcg] = useState("");
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    if (slug) {
      api.get(`/products/${slug}`).then(({ data }) => {
        setPrefill(data);
        setVialMg("");
        toast.info("Product loaded. Enter vial strength manually.");
      }).catch(() => {});
    }
  }, [slug]);

  const vial = parseFloat(vialMg) || 0;
  const dil = parseFloat(diluentMl) || 0;
  const dose = parseFloat(doseMcg) || 0;

  const mgPerMl = dil > 0 ? vial / dil : 0;
  const mcgPerMl = mgPerMl * 1000;
  const mcgPerUnit = mcgPerMl / 100;
  const doseVolumeMl = mcgPerMl > 0 && dose > 0 ? dose / mcgPerMl : 0;
  const doseUnits = doseVolumeMl * 100;
  const liquidPct = Math.min(90, Math.max(8, (dil / 10) * 90));

  // converter
  const [convIn, setConvIn] = useState("");
  const [convFrom, setConvFrom] = useState("mg");
  const convVal = parseFloat(convIn) || 0;
  const conversions = {
    mg: [["mcg", convVal * 1000], ["g", convVal / 1000]],
    mcg: [["mg", convVal / 1000], ["g", convVal / 1e6]],
    mL: [["μL", convVal * 1000]],
    "μL": [["mL", convVal / 1000]],
    "mg/mL": [["mcg/mL", convVal * 1000], ["mcg/μL", convVal]],
  };

  const save = async () => {
    if (!user) return toast.error("Sign in to save calculations");
    if (!vial || !dil) return toast.error("Enter vial and diluent first");
    await api.post("/calc/save", {
      label: prefill ? prefill.name : "Manual calculation",
      vial_mg: vial, diluent_ml: dil, desired_dose_mcg: dose || null,
      result: { mgPerMl, mcgPerMl, doseVolumeMl, doseUnits },
    });
    toast.success("Calculation saved to My Lab");
  };

  const copy = () => {
    navigator.clipboard.writeText(`${vial}mg / ${dil}mL = ${mgPerMl.toFixed(3)} mg/mL (${mcgPerMl.toFixed(0)} mcg/mL)${dose ? ` | ${dose}mcg = ${doseUnits.toFixed(1)} units` : ""}`);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-6xl mx-auto">
      <div className="mono-label mb-3">Lab instrument</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-3">Precision Calculator</h1>
      <p className="text-slate-400 text-sm max-w-xl mb-8">Concentration arithmetic for research values you supply. CG Labz does not recommend or prepopulate doses.</p>

      <div className="flex gap-1 border-b border-white/10 mb-8">
        {[["calc", "Calculator"], ["convert", "Converter"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`calc-tab-${k}`}
            className={`px-5 py-2.5 text-xs font-mono uppercase tracking-widest ${tab === k ? "text-cyan-300 border-b-2 border-cyan-400" : "text-slate-500"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "calc" && (
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="glass rounded-3xl p-8 relative overflow-hidden">
            <div className="scan-line" />
            <div className="mono-label mb-4">Vial visualization</div>
            <div className="vial-outer w-32 h-56 mx-auto relative">
              <div className="vial-cap" />
              <div className="vial-liquid" style={{ height: `${liquidPct}%` }} />
              <div className="absolute inset-x-0 bottom-4 text-center">
                <div className="text-[10px] font-mono text-white/90">{vial || "—"} mg</div>
                <div className="text-[9px] font-mono text-cyan-300">{dil || "—"} mL</div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-2 text-center">
              {[["VIAL", vial || "—", "mg"], ["DILUENT", dil || "—", "mL"], ["CONC.", mgPerMl ? mgPerMl.toFixed(3) : "—", "mg/mL"]].map(([l, v, u]) => (
                <div key={l} className="rounded-xl border border-white/10 p-3">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{l}</div>
                  <div className="font-mono text-lg text-cyan-300 mt-1">{v}</div>
                  <div className="text-[9px] font-mono text-slate-500">{u}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-5">
            {prefill && <div className="glass rounded-2xl p-4 text-xs font-mono text-cyan-300">Loaded product: {prefill.name}</div>}
            <div className="glass rounded-2xl p-6 space-y-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Vial amount (mg)</label>
                <Input type="number" value={vialMg} onChange={(e) => setVialMg(e.target.value)} placeholder="e.g. 10" data-testid="calc-vial-mg-input"
                  className="mt-2 bg-black/40 border-white/10 font-mono" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Diluent volume (mL)</label>
                <Input type="number" value={diluentMl} onChange={(e) => setDiluentMl(e.target.value)} placeholder="e.g. 2" data-testid="calc-bac-water-ml-input"
                  className="mt-2 bg-black/40 border-white/10 font-mono" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Target quantity (mcg) — optional, you supply this</label>
                <Input type="number" value={doseMcg} onChange={(e) => setDoseMcg(e.target.value)} placeholder="Enter manually" data-testid="calc-desired-mcg-input"
                  className="mt-2 bg-black/40 border-white/10 font-mono" />
              </div>
            </div>

            {mgPerMl > 0 && (
              <div className="glass rounded-2xl p-6 space-y-3" data-testid="calc-syringe-units-result">
                <div className="mono-label">Result</div>
                <div className="grid grid-cols-2 gap-3 font-mono text-sm">
                  <div className="rounded-xl border border-white/10 p-3"><div className="text-[9px] uppercase text-slate-500 mb-1">Concentration</div><div className="text-cyan-300">{mgPerMl.toFixed(4)} mg/mL</div><div className="text-slate-400 text-xs">{mcgPerMl.toFixed(0)} mcg/mL</div></div>
                  <div className="rounded-xl border border-white/10 p-3"><div className="text-[9px] uppercase text-slate-500 mb-1">Per unit (U-100)</div><div className="text-cyan-300">{mcgPerUnit.toFixed(2)} mcg</div></div>
                  {dose > 0 && <>
                    <div className="rounded-xl border border-white/10 p-3"><div className="text-[9px] uppercase text-slate-500 mb-1">Volume for {dose} mcg</div><div className="text-cyan-300">{doseVolumeMl.toFixed(3)} mL</div></div>
                    <div className="rounded-xl border border-white/10 p-3"><div className="text-[9px] uppercase text-slate-500 mb-1">Syringe units</div><div className="text-cyan-300">{doseUnits.toFixed(1)} units</div></div>
                  </>}
                </div>
                <p className="text-[10px] font-mono text-slate-500">Formula: concentration = vial mg ÷ diluent mL. Syringe units = (target mcg ÷ mcg/mL) × 100.</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => { setVialMg(""); setDiluentMl(""); setDoseMcg(""); }} variant="outline" data-testid="calc-reset-btn" className="border-white/10 text-slate-300 rounded-xl"><RotateCcw size={13} className="mr-1" />Reset</Button>
              <Button onClick={copy} variant="outline" data-testid="calc-copy-btn" className="border-white/10 text-slate-300 rounded-xl"><Copy size={13} className="mr-1" />Copy</Button>
              <Button onClick={save} variant="outline" data-testid="calc-save-btn" className="border-white/10 text-slate-300 rounded-xl"><Save size={13} className="mr-1" />Save</Button>
              <Button onClick={() => window.print()} variant="outline" data-testid="calc-print-btn" className="border-white/10 text-slate-300 rounded-xl"><Printer size={13} className="mr-1" />Print</Button>
            </div>
          </div>
        </div>
      )}

      {tab === "convert" && (
        <div className="grid md:grid-cols-2 gap-8 max-w-3xl">
          <div className="glass rounded-2xl p-6 space-y-4">
            <div className="mono-label">Input</div>
            <Input type="number" value={convIn} onChange={(e) => setConvIn(e.target.value)} placeholder="Value" data-testid="converter-input" className="bg-black/40 border-white/10 font-mono" />
            <select value={convFrom} onChange={(e) => setConvFrom(e.target.value)} data-testid="converter-unit"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm font-mono text-slate-300">
              {Object.keys(conversions).map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="mono-label mb-4">Output</div>
            <div className="space-y-3">
              {(conversions[convFrom] || []).map(([u, v]) => (
                <div key={u} className="flex justify-between border-b border-white/5 pb-2 font-mono text-sm">
                  <span className="text-slate-500">{u}</span>
                  <span className="text-cyan-300">{v.toLocaleString(undefined, { maximumFractionDigits: 6 })}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
